import Phaser from "phaser";
import { installLetterboxFit } from "../ui/responsive";
import mapConfig from "../data/mapConfig.json";
import unitsData from "../data/units.json";
import towersData from "../data/towers.json";
import enemiesData from "../data/enemies.json";

const phase1AnimatedUnitIds = new Set(["spearman_new", "alchemist", "standard_bearer", "engineer"]);
const phase1AnimatedEnemyIds = new Set(["necromancer", "bomber", "heavy_ram", "knight", "black_assassin", "armored_beast", "plague_bishop"]);

export class BootScene extends Phaser.Scene {
  constructor() {
    super("BootScene");
  }

  preload() {
    // Phaser's loader (v3.90) can intermittently stall: after the first batch of parallel
    // downloads (capped at `maxParallelDownloads`, default 32) completes, the loader sometimes
    // fails to pull the next batch from its `list` — leaving files stranded with nothing
    // inflight and no error, so BootScene.create() never runs and the screen stays black.
    // Raising the cap above the total file count loads everything in a single batch, so there
    // is no "next batch" step that can stall. (Confirmed: the stall always froze at exactly
    // `maxParallelDownloads` files completed.)
    this.load.maxParallelDownloads = 256;

    this.load.image(mapConfig.mapAssetKey, mapConfig.mapImagePath);
    this.load.image("player_castle", "assets/castles/player_castle.png");
    this.load.image("player_castle_damaged", "assets/castles/player_castle_damaged.png");
    this.load.image("ai_fortress", "assets/castles/ai_fortress.png");
    this.load.image("ai_fortress_damaged", "assets/castles/ai_fortress_damaged.png");

    for (const unit of unitsData) {
      this.load.image(unit.id, unit.sprite);
      if (phase1AnimatedUnitIds.has(unit.id)) {
        this.load.spritesheet(`${unit.id}_walk`, unit.sprite.replace(".png", "_sheet.png"), {
          frameWidth: 128,
          frameHeight: 128
        });
      } else {
        this.load.spritesheet(`${unit.id}_walk`, unit.sprite.replace(".png", "_sheet.png"), {
          frameWidth: 96,
          frameHeight: 96
        });
      }
    }

    for (const enemy of enemiesData) {
      this.load.image(`enemy_${enemy.id}`, enemy.sprite);
      if (phase1AnimatedEnemyIds.has(enemy.id)) {
        this.load.spritesheet(`enemy_${enemy.id}_walk`, enemy.sprite.replace(".png", "_sheet.png"), {
          frameWidth: 128,
          frameHeight: 128
        });
      } else {
        this.load.spritesheet(`enemy_${enemy.id}_walk`, enemy.sprite.replace(".png", "_sheet.png"), {
          frameWidth: 96,
          frameHeight: 96
        });
      }
    }

    for (const tower of towersData) {
      this.load.image(tower.id, tower.sprite);
    }

    this.load.image("hud_panel", "assets/ui/hud_panel.png");
    this.load.image("build_bar", "assets/ui/build_bar.png");
    this.load.image("build_card", "assets/ui/build_card.png");
    this.load.image("build_card_selected", "assets/ui/build_card_selected.png");
    this.load.image("build_card_disabled", "assets/ui/build_card_disabled.png");
    this.load.image("lane_panel", "assets/ui/lane_panel.png");
    this.load.image("commander_panel", "assets/ui/commander_panel.png");
    this.load.image("commander_portrait", "assets/ui/commander_portrait.png");
    this.load.image("gold_icon", "assets/ui/gold_icon.png");
    this.load.image("counter_frame", "assets/ui/counter_frame.png");
    this.load.image("hp_frame", "assets/ui/hp_frame.png");
    this.load.image("tower_pad", "assets/pads/stone_foundation.png");
    this.load.image("tower_pad_hover", "assets/pads/rune_pad.png");
    this.load.image("construction_pad", "assets/pads/construction_platform.png");
    this.load.image("spawn_warning", "assets/effects/spawn_warning.png");
    this.load.image("projectile_player", "assets/effects/projectile_player.png");
    this.load.image("projectile_enemy", "assets/effects/projectile_enemy.png");
    this.load.image("projectile_frost", "assets/effects/projectile_frost.png");
    this.load.image("hit_spark", "assets/effects/hit_spark.png");
  }

  create() {
    installLetterboxFit(this);
    this.scene.start("MenuScene");
  }
}
